import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { AuthService } from '../../../services/api/authService';
import { ResetPasswordData } from '../../../types/auth';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [codeSent, setCodeSent] = useState(false);
  const [formData, setFormData] = useState<ResetPasswordData>({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await AuthService.forgotPassword(formData.email);
      setCodeSent(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await AuthService.confirmForgotPassword({
        email: formData.email,
        code: formData.code || '',
        newPassword: formData.newPassword || ''
      });
      navigate('/login', {
        state: {
          message: 'Password reset successful! Please log in with your new password.'
        }
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      sx={{
        height: '100vh',
        backgroundColor: '#A46A3F', // Same main background color as login
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Grid item xs={10} sm={8} md={4}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" textAlign="center" sx={{ mb: 3 }}>
            {codeSent ? 'Reset your password' : 'Forgot your password?'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!codeSent ? (
            <Box
              component="form"
              onSubmit={handleSendCode}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                name="email"
                type="email"
                label="Email address"
                variant="outlined"
                fullWidth
                required
                value={formData.email}
                onChange={handleChange}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: '#7A4F2D',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#6B4429' }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                    Sending...
                  </>
                ) : (
                  'Send reset code'
                )}
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleResetPassword}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                name="code"
                type="text"
                label="Verification code"
                variant="outlined"
                fullWidth
                required
                value={formData.code}
                onChange={handleChange}
              />
              <TextField
                name="newPassword"
                type="password"
                label="New password"
                variant="outlined"
                fullWidth
                required
                value={formData.newPassword}
                onChange={handleChange}
              />
              <TextField
                name="confirmPassword"
                type="password"
                label="Confirm new password"
                variant="outlined"
                fullWidth
                required
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: '#7A4F2D',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#6B4429' }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                    Resetting...
                  </>
                ) : (
                  'Reset password'
                )}
              </Button>
            </Box>
          )}

          <Box textAlign="center" sx={{ mt: 2 }}>
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              color="primary"
            >
              Back to sign in
            </Link>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
